import { ComponentProps, ReactElement } from 'react'

import type { DocumentContext } from 'next/document'

import { globalBoostrapPropsWrapper, globalBootstrap } from '~/utils/globalBootstrap'

import ChefsMenuPage from '~/components/ChefsMenu/ChefsMenuPage'
import { getChefsMenuLayout } from '~/components/ChefsMenu/ChefsMenuPage/ChefsMenuLayout'
import { getReservableExtraServerSideProps } from '~/components/ChefsMenu/ChefsMenuPage/ChefsMenuPageUtils'
import RestaurantPage from '~/components/Restaurant/RestaurantPage'
import { getRestaurantPageLayout } from '~/components/Restaurant/RestaurantPage/RestaurantPageLayout'
import { getRestaurantPageServerSideProps } from '~/components/Restaurant/RestaurantPage/RestaurantPageUtils'
import { ResolveUrlSlugDocument, ResolveUrlSlugQuery, ResolveUrlSlugQueryVariables } from '~/generated/graphql'

export const getServerSideProps = async (ctx: DocumentContext) => {
  const { apolloClient } = await globalBootstrap({ ctx })
  const { query } = ctx
  const { slug } = query

  if (!slug) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    }
  }

  const { data } = await apolloClient.query<ResolveUrlSlugQuery, ResolveUrlSlugQueryVariables>({
    query: ResolveUrlSlugDocument,
    variables: {
      slug: slug.toString(),
    },
    errorPolicy: 'all',
  })

  if (!data || !data.resolveUrlSlug) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    }
  }

  const {
    resolveUrlSlug: { objectId, objectType },
  } = data

  if (objectType === 'Restaurant') {
    return getRestaurantPageServerSideProps({ restaurantId: objectId, apolloClient, additionalProps: { objectType, query } })
  } else if (objectType === 'ReservableExtra') {
    return getReservableExtraServerSideProps({ reservableExtraId: objectId, apolloClient, additionalProps: { objectType } })
  }

  return globalBoostrapPropsWrapper(
    { apolloClient },
    {
      props: {
        resource: '',
      },
    }
  )
}

const ProductPage = (props: any) => {
  return props.objectType === 'Restaurant' ? <RestaurantPage {...props} /> : <ChefsMenuPage {...props} />
}

export default ProductPage

type GetLayoutParam =
  | ReactElement<ComponentProps<typeof RestaurantPage> & { objectType: 'Restaurant' }>
  | ReactElement<ComponentProps<typeof ChefsMenuPage> & { objectType: 'ReservableExtra' }>

ProductPage.getLayout = function (page: GetLayoutParam) {
  const type = page.props.objectType

  if (type === 'ReservableExtra') {
    const props = page.props
    return getChefsMenuLayout({ ...page, props })
  } else {
    const props = page.props
    return getRestaurantPageLayout({ ...page, props })
  }
}
